/**
 * Structured Data Helper Functions
 * Utility functions for generating Schema.org structured data
 */

/**
 * Format date to ISO 8601 format
 * @param {string|Date} date - Date to format
 * @returns {string|null} - Formatted date or null
 */
export const formatDateISO = date => {
  if (!date) return null
  try {
    return new Date(date).toISOString()
  } catch (e) {
    console.warn('Invalid date format:', date)
    return null
  }
}

/**
 * Build absolute URL from path
 * @param {string} path - Relative or absolute path
 * @param {string} baseUrl - Base URL
 * @returns {string} - Absolute URL
 */
export const absoluteUrl = (path, baseUrl) => {
  if (!path) return baseUrl
  if (path.startsWith('http://') || path.startsWith('https://')) return path

  const cleanPath = path.startsWith('/') ? path : `/${path}`
  const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl

  return `${cleanBase}${cleanPath}`
}

/**
 * Build ImageObject schema
 * @param {Object} image - Image object with src, width, height, alt
 * @param {string} baseUrl - Base URL
 * @returns {Object|null} - ImageObject schema or null
 */
export const imageObject = (image, baseUrl) => {
  if (!image) return null

  const imageUrl = image.src || image.url
  if (!imageUrl) return null

  const schema = {
    '@type': 'ImageObject',
    'url': absoluteUrl(imageUrl, baseUrl)
  }

  if (image.width) schema.width = parseInt(image.width)
  if (image.height) schema.height = parseInt(image.height)
  // Caption = SOLO l'alt reale (già include il fallback al titolo validato, applicato a
  // monte nel backend). Mai il title/filename dell'asset. Omessa se l'alt è vuoto.
  if (image.alt) schema.caption = image.alt

  return schema
}

/**
 * Build multiple ImageObject schemas from array
 * @param {Array} images - Array of image objects
 * @param {string} baseUrl - Base URL
 * @returns {Array} - Array of ImageObject schemas
 */
export const imageObjects = (images, baseUrl) => {
  if (!Array.isArray(images) || images.length === 0) return []

  return images
    .map(img => imageObject(img, baseUrl))
    .filter(Boolean)
}

/**
 * Clean and truncate text for descriptions
 * @param {string} text - Text to clean
 * @param {number} maxLength - Maximum length (default: 5000)
 * @returns {string|null} - Cleaned text or null
 */
export const cleanText = (text, maxLength = 5000) => {
  if (!text) return null

  // Remove HTML tags
  let cleaned = text.replace(/<[^>]*>/g, '')

  // Remove excessive whitespace
  cleaned = cleaned.replace(/\s+/g, ' ').trim()

  // Truncate if needed
  if (cleaned.length > maxLength) {
    cleaned = cleaned.substring(0, maxLength - 3) + '...'
  }

  return cleaned || null
}

/**
 * Build PostalAddress schema
 * @param {Object} address - Address object
 * @returns {Object|null} - PostalAddress schema or null
 */
export const postalAddress = address => {
  if (!address) return null

  const schema = {
    '@type': 'PostalAddress'
  }

  if (address.storeAddress || address.streetAddress) {
    schema.streetAddress = address.storeAddress || address.streetAddress
  }
  if (address.city) schema.addressLocality = address.city
  if (address.province) schema.addressRegion = address.province
  if (address.zipCode || address.postalCode) {
    schema.postalCode = address.zipCode || address.postalCode
  }
  if (address.country) schema.addressCountry = address.country

  // Return null if no fields were added (only @type)
  if (Object.keys(schema).length === 1) return null

  return schema
}

/**
 * Build GeoCoordinates schema
 * @param {number} latitude - Latitude
 * @param {number} longitude - Longitude
 * @returns {Object|null} - GeoCoordinates schema or null
 */
export const geoCoordinates = (latitude, longitude) => {
  if (!latitude || !longitude) return null

  return {
    '@type': 'GeoCoordinates',
    'latitude': parseFloat(latitude),
    'longitude': parseFloat(longitude)
  }
}

/**
 * Build AggregateRating schema
 * @param {number} ratingValue - Rating value
 * @param {number} reviewCount - Number of reviews
 * @param {number} bestRating - Best possible rating (default: 5)
 * @returns {Object|null} - AggregateRating schema or null
 */
export const aggregateRating = (ratingValue, reviewCount = null, bestRating = 5) => {
  if (!ratingValue || ratingValue <= 0) return null

  const schema = {
    '@type': 'AggregateRating',
    'ratingValue': parseFloat(ratingValue),
    'bestRating': parseInt(bestRating)
  }

  if (reviewCount && reviewCount > 0) {
    schema.reviewCount = parseInt(reviewCount)
  }

  return schema
}

/**
 * Build Organization/Brand schema
 * @param {string} name - Organization name
 * @param {string} url - Organization URL
 * @returns {Object} - Organization schema
 */
export const organizationSchema = (name = 'Veneta Cucine', url = 'https://www.venetacucine.com') => {
  return {
    '@type': 'Organization',
    name,
    url
  }
}

/**
 * Remove undefined/null values from object
 * @param {Object} obj - Object to clean
 * @returns {Object} - Cleaned object
 */
export const cleanSchema = obj => {
  if (!obj || typeof obj !== 'object') return obj

  const cleaned = {}

  for (const [ key, value ] of Object.entries(obj)) {
    if (value !== null && value !== undefined) {
      if (typeof value === 'object' && !Array.isArray(value)) {
        const cleanedValue = cleanSchema(value)
        if (Object.keys(cleanedValue).length > 0) {
          cleaned[key] = cleanedValue
        }
      } else if (Array.isArray(value)) {
        const cleanedArray = value.map(item =>
          typeof item === 'object' ? cleanSchema(item) : item
        ).filter(item => item !== null && item !== undefined)

        if (cleanedArray.length > 0) {
          cleaned[key] = cleanedArray
        }
      } else {
        cleaned[key] = value
      }
    }
  }

  return cleaned
}
